/**
 * Assign Job Dialog Component
 * Dialog for assigning a job to a customer
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Search, CheckCircle2 } from 'lucide-react';
import { jobsApi } from '@/lib/api/jobs.api';
import type { Job } from '@repo/types';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AssignJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  onAssign: (jobId: string, reason?: string) => Promise<void>;
}

export function AssignJobDialog({
  open,
  onOpenChange,
  customerId: _customerId,
  customerName,
  onAssign,
}: AssignJobDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [reason, setReason] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch jobs with search
  const { data: jobsData, isLoading: isLoadingJobs } = useQuery({
    queryKey: ['jobs', 'search', searchQuery],
    queryFn: () =>
      jobsApi.getJobs(
        searchQuery ? { search: searchQuery } : undefined,
        { page: 1, limit: 20 }
      ),
    enabled: open && searchQuery.length >= 2,
  });

  const jobs = jobsData?.jobs || [];

  const handleAssign = async () => {
    if (!selectedJob) return;

    setError(null);
    setIsAssigning(true);

    try {
      await onAssign(selectedJob.id, reason.trim() || undefined);
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign job');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedJob(null);
    setReason('');
    setError(null);
    setSuccess(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Assign Job to {customerName}</DialogTitle>
          <DialogDescription>
            Search for a job and assign it to this customer. The job will be recommended to them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="space-y-2">
            <Label htmlFor="job-search">Search Jobs</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="job-search"
                placeholder="Search by title, company, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchQuery.length > 0 && searchQuery.length < 2 && (
              <p className="text-xs text-muted-foreground">Enter at least 2 characters to search</p>
            )}
          </div>

          {/* Job List */}
          {searchQuery.length >= 2 && (
            <div className="space-y-2">
              <Label>Select a Job</Label>
              <ScrollArea className="h-[300px] rounded-md border p-4">
                {isLoadingJobs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : jobs.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No jobs found. Try a different search term.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {jobs.map((job) => (
                      <div
                        key={job.id}
                        onClick={() => setSelectedJob(job)}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                          selectedJob?.id === job.id
                            ? 'border-primary bg-primary/5'
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">{job.title}</h4>
                              {selectedJob?.id === job.id && (
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{job.company}</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="text-xs">
                                {job.location}
                              </Badge>
                              {job.category && (
                                <Badge variant="secondary" className="text-xs">
                                  {job.category}
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-xs">
                                {job.jobType}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}

          {/* Selected Job Display */}
          {selectedJob && (
            <div className="p-4 rounded-lg border bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Selected Job</span>
              </div>
              <p className="font-semibold">{selectedJob.title}</p>
              <p className="text-sm text-muted-foreground">{selectedJob.company} • {selectedJob.location}</p>
            </div>
          )}

          {/* Reason Input */}
          {selectedJob && (
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                placeholder="Add a reason for assigning this job..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground">{reason.length}/1000 characters</p>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Job assigned successfully!
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isAssigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedJob || isAssigning || success}
          >
            {isAssigning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Assigning...
              </>
            ) : (
              'Assign Job'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

